import clsx from "clsx";
import { isMetaOrCtrlKey, isShiftKey } from "csi/utility/eventUtility";
import { useDragWithPosition } from "designSystem/dnd/index";
import React, { CSSProperties, PropsWithChildren, ReactElement, useLayoutEffect, useRef } from "react";
import { useDragDropManager } from "react-dnd";
import { DndItemWithId, handleDragHighlight, useDndStore } from "../store/dnd-store";
import dndCss from "../styles/dnd.module.css";
import { DndEndFunction } from "../types";
import { observer } from "mobx-react-lite";

export interface DragWrapperProps<T extends DndItemWithId> extends PropsWithChildren {
	/** specifies whether the item can initiate a drag, true by default */
	canDrag?: boolean;
	/** disable default light blue background */
	disableBgColorChangeOnDrag?: boolean;
	/** item to drag */
	dragItem: T;
	/** optional drag handle */
	dragHandle?: ReactElement;
	/** optional drag handle */
	dragHandleStyle?: CSSProperties;
	/** type of item being dragged */
	dragType: string;
	/** drop zone is full screen or local to drop wrapper component */
	isFullScreen?: boolean;
	/** is item selected for multi select only */
	isItemSelected?: boolean;
	/** multi select */
	isMultiSelect?: boolean;
	/** on click handler */
	onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	/** this is called when the dragging stops */
	onEndDrag?: DndEndFunction<T>;
	/** on mouse down handler */
	onMouseDown?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

export const DragWrapper = observer(function DragWrapper<T extends DndItemWithId>({
	canDrag = true,
	children,
	disableBgColorChangeOnDrag,
	dragItem,
	dragHandle,
	dragHandleStyle,
	dragType,
	isFullScreen,
	isItemSelected,
	isMultiSelect,
	onClick,
	onEndDrag,
	onMouseDown,
	...otherProps
}: DragWrapperProps<T>) {
	const { drag, handlerId, isDragging } = useDragWithPosition<T>(
		canDrag,
		dragItem,
		dragType,
		isFullScreen,
		onEndDrag,
	);
	const ref = useRef<HTMLDivElement>(null);

	// TODO: move more props to the store
	const { disableMultiSelectHighlight } = useDndStore();

	// Assign the drag ref to the custom drag handle if provided, otherwise to the wrapper
	const handleRef = dragHandle ? useRef<HTMLDivElement>(null) : ref;
	drag(handleRef);

	// if there is a highlight wrapper, light it up, otherwise, use current
	useLayoutEffect(() => {
		handleDragHighlight(ref, isDragging, disableBgColorChangeOnDrag);
	}, [isDragging]);

	// add cancelability to drag
	const manager = useDragDropManager();
	function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
		if (event.key === "Escape" && isDragging) {
			manager.getActions().endDrag();
		}
	}

	return (
		<div
			className={clsx(
				!dragHandle && canDrag && dndCss.dragCursor,
				!disableBgColorChangeOnDrag && isDragging && dndCss.isDragging,
				!disableMultiSelectHighlight && isItemSelected && dndCss.isSelected,
			)}
			data-handler-id={handlerId}
			onClick={onClick}
			onKeyDown={onKeyDown}
			onMouseDown={onMouseDown}
			ref={ref}
			{...otherProps}
		>
			<div style={{ position: "relative" }}>
				{dragHandle && (
					React.cloneElement(dragHandle, { ref: handleRef, style: dragHandleStyle })
				)}
				{children}
			</div>
		</div>
	);
});